import { isFetchError, isLocalStorageError } from "./errorsHelpers"

/**
 * There could not be 1 central place to catch and process all errors.
 * They may trip to error trackers from different streams. But all
 * errors should be processed by this central errors manager.
 * The flows are:
 * EARLY ERRORS -> caught in head -> errorsManager -> adapters -> Bugsnag, Sentry    
 * OUTER ERRORS -> auto-caught in adapters -> delegated to errorsManager -> adapter which delegated -> Bugsnag, Sentry
 * FETCH ERRORS -> patched in head -> auto-caught in adapters -> delegated to errorsManager -> adapter which delegated -> Bugsnag, Sentry    
 * OUR CODE ERRORS -> caught manually -> errorsManager -> adapters -> Bugsnag, Sentry    
 */

/**
 * Symbol used to store pipeline-specific metadata on error objects.
 * The data is non-enumerable and meant only for internal use.
 * @type {symbol}
 */
export const ERROR_PIPE_META_DATA = Symbol("pipeMetaData")

/**
 * Symbol used to mark the adapter that originally intercepted
 * an error before delegating it back to the errors manager.
 * Allows the manager to route the error back only to that specific adapter.
 * @type {symbol}
 */
export const ERROR_SOURCE_ADAPTER_NAME = Symbol("sourceAdapterName")

export const errorsManager = {
  /**
   * Array of initialized error tracking adapters.
   * Each adapter must implement a shared interface:
   *   - adapterName: string
   *   - init()?
   *   - reportError(error)?
   *   - reportLocalStorageError(error)?
   *   - reportFetchError(error)?
   *   - reportNonError(error)?
   */
  errorTrackingAdapters: [],

  /**
   * Initializes the error manager and all adapters.
   * Also processes early errors collected before the main bundle.
   *
   * @param {Object} [options]
   * @param {Array<Object>} [options.adapters] List of adapters to register.
   */
  init({ adapters } = {}) {

    // Init adapters
    this.errorTrackingAdapters = adapters || []
    this.errorTrackingAdapters.forEach((adapter) => {
      if (typeof adapter?.init === "function") {
        try { adapter.init() } catch (e) {
          console.warn(`[CPPV3 errorsManager] Adapter init failed:`, e)
        }
      }
    })

    // Process early errors collected in the <head> inline script
    // (triggered before main JS bundle init)
    if (window.pipeEarlyErrors?.length) {
      window.pipeEarlyErrors.forEach(error => {
        if (isLocalStorageError(error)) {
          this.reportLocalStorageError(error)
        } else if (isFetchError(error)) {
          this.reportFetchError(error)
        } else {
          this.reportError(error)
        }
      })
    }
  },

  /**
   * Calls a specific reporting method on all registered adapters.
   * If an error was originally intercepted by one adapter and delegated
   * back to the manager, this ensures only that adapter receives it again.
   *
   * @param {string} methodName Name of adapter method to invoke.
   * @param {Error} error Error being processed.
   */
  callAdaptersMethod(methodName, error) {
    this.errorTrackingAdapters.forEach(adapter => {
      const method = adapter?.[methodName]

      if (typeof method === "function") {
        try {
          const adapterName = adapter.adapterName
          const errorSourceAdapterName = error[ERROR_SOURCE_ADAPTER_NAME]

          // Avoid re-reporting across all services
          if (errorSourceAdapterName && errorSourceAdapterName !== adapterName) {
            return
          }

          method.call(adapter, error)
        } catch (e) {
          console.warn(`[CPPV3 errorManager] Adapter method "${methodName}" failed:`, e)
        }
      }
    })
  },

  /**
   * Extracts the pipeline ID from the global pipelineConfig.
   * @returns {string} Pipeline ID
   */
  getPipelineID() {
    const url = pipelineConfig.orderApiUrl
    const pipelinesMatchedString = url.match(/pipelines\/+\d+\//)

    return pipelinesMatchedString[0].split("/")[1] // Takes the number from the string - which is pipeline ID
  },

  /**
   * Attaches internal error metadata which is stored
   * under a Symbol key to avoid leaking into trackers.
   *
   * @param {Object} params
   * @param {Error} params.error Error to extend.
   * @param {Object} [params.metaData] Additional data.
   */
  addErrorPipeMetaData({ error, metaData }) {
    const pipeMetaData = {
      pipelineId: this.getPipelineID(),
      primerSdkVersion: pipelineConfig?.primerSdkVersion
    }

    if (window.Alpine) {
      const store = window.Alpine.store("data")
      Object.assign(pipeMetaData, {
        customerId: store?.values?.customerId,
        orderId: store?.values?.id,
        userId: store?.values?.userId,
        email: store?.values?.email,
        ...metaData
      })
    }

    // Non enumerable to skip data leaks to unwanted places.
    Object.defineProperty(error, ERROR_PIPE_META_DATA, {
      value: pipeMetaData,
      writable: true,
      configurable: true,
      enumerable: false
    })
  },

  /**
   * Set of already reported localStorage error messages.
   * Prevents redundant spam when localStorage repeatedly fails.
   * If localStorage failed once on the page load - 
   * there is no sense to report same error many times in a row, 
   * as localStorage is used in multiple places within the code during page init.
   * @type {Set<string>}
   */
  reportedLocalStorageErrors: new Set(),

  /**
   * Normalizes and reports localStorage-related errors.
   * Ensures deduplication and grouping.
   * Calls all adapters corresponding method.
   *
   * @param {Error} error
   */
  reportLocalStorageError(error) {
    if (!error) return

    const msg = error.message || String(error)

    // Skip if already reported
    if (this.reportedLocalStorageErrors.has(msg)) {
      console.log('[CPPV3] localStorage: duplicated error report suppressed:', msg)
      return
    }

    this.reportedLocalStorageErrors.add(msg)

    // Overwrite error to our own specific type - to not bloat Bugsnag
    // with duplicated typical errors which then grouped differently
    const normalizedError = new Error(`localStorage problem: ${error?.message}`)

    // Keep the original trace
    if (error?.stack) {
      normalizedError.stack = error.stack
    }

    this.addErrorPipeMetaData({ error: normalizedError })

    this.callAdaptersMethod("reportLocalStorageError", normalizedError)
  },

  /**
   * Normalizes fetch errors, adding request metadata and custom formatting.
   * Calls all adapters corresponding method.
   *
   * @param {Error} error
   */
  reportFetchError(error) {
    if (!error) return

    // Overwrite error to our own specific type - to not bloat Bugsnag
    // with duplicated typical errors which then grouped differently
    const normalizedError = 
      new Error(`${error?.cppv3FetchErrorData?.method}: ${error?.cppv3FetchErrorData?.url} -> ${error?.message}`)
  
    // Keep the original trace
    if (error?.stack) {
      normalizedError.stack = error.stack
    }

    this.addErrorPipeMetaData({ error: normalizedError, metaData: error?.cppv3FetchErrorData })

    this.callAdaptersMethod("reportFetchError", normalizedError)
  },

  /**
   * Default error reporting method for regular JS exceptions.
   *
   * @param {Error} error
   * @param {Object} [metaData]
   */
  reportError(error, metaData) {
    if (!error) return

    // Some errors do not include stack
    error.stack = error.stack || new Error().stack

    this.addErrorPipeMetaData({ error, metaData })

    this.callAdaptersMethod("reportError", error)
  },

  /**
   * Reports a custom error that may be a string or Error instance.
   * Useful to just ping and understand the impact, or to manually 
   * throw error, for the sake to debug in production for some random cases.
   *
   * @param {string|Error} error
   * @param {Object} [metaData]
   */
  reportCustomError(error, metaData) {
    if (!error) return

    let customError

    if (error instanceof Error) {
      customError = error
    } else {
      customError = new Error(error)
    }

    this.addErrorPipeMetaData({ error: customError, metaData })

    this.callAdaptersMethod("reportError", customError)
  },
  
  /**
   * Reports a non-Error rejection (like ProgressEvent, DOMException, etc.)
   * captured by unhandledrejection.
   *
   * @param {Error} error Already converted to Error by the adapter.
   * @param {Object} [metaData]
   */
  reportNonError(error, metaData) {
    if (!error) return
    
    // Some errors do not include stack
    error.stack = error.stack || new Error().stack
    
    this.addErrorPipeMetaData({ error, metaData })
    
    this.callAdaptersMethod("reportNonError", error)
  },
}
